import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  InputAdornment,
  Grid,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Supplier, CreateSupplierRequest, UpdateSupplierRequest } from '../types/supplier';
import { suppliersAPI } from '../services/api';
import { useSuppliersList, useDeletedSuppliers, useInvalidateSuppliers } from '../hooks/queries/useSuppliers';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from './CustomTablePagination';
import { extractErrorMessage } from '../utils/errorHandler';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';

const CONFIG_COLOR = '#1976D2';

const SUPPLIER_FILTER_SCHEMA = {
  code:         { type: 'text', param: 'code' },
  name:         { type: 'text', param: 'name' },
  contact_name: { type: 'text', param: 'contact_name' },
  email:        { type: 'text', param: 'email' },
  phone:        { type: 'text', param: 'phone' },
  mobile:       { type: 'text', param: 'mobile' },
  is_active:    { type: 'select', param: 'is_active' },
} as const;

const SUPPLIER_STATUT_OPTIONS = [
  { value: 'true', label: 'Actif' },
  { value: 'false', label: 'Inactif' },
];


const SupplierManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<CreateSupplierRequest>({
    code: '',
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    mobile: '',
    ice: '',
    address: '',
    is_active: true,
  });
  const [formError, setFormError] = useState('');
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(SUPPLIER_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  // Cached list + deleted list; mutations invalidate to refetch.
  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: searchTerm || undefined,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, filters]);
  const listQuery = useSuppliersList(listParams);
  const deletedQuery = useDeletedSuppliers(tabValue === 1);
  const invalidate = useInvalidateSuppliers();
  const suppliers = listQuery.data?.items ?? [];
  const totalSuppliers = listQuery.data?.total ?? 0;
  const deletedSuppliers = deletedQuery.data?.items ?? [];
  const totalDeletedSuppliers = deletedQuery.data?.total ?? 0;
  const loading = tabValue === 0 ? listQuery.isInitialLoading : deletedQuery.isInitialLoading;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRestoreSupplier = async (supplier: Supplier) => {
    try {
      await suppliersAPI.restore(supplier.id);
      showSuccess('Fournisseur restauré avec succès');
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleOpenDialog = async (supplier?: Supplier) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        code: supplier.code,
        name: supplier.name,
        contact_name: supplier.contact_name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        mobile: supplier.mobile || '',
        ice: supplier.ice || '',
        address: supplier.address || '',
        is_active: supplier.is_active,
      });
    } else {
      setEditingSupplier(null);
      // Get the next auto-generated code
      try {
        const { code } = await suppliersAPI.getNextCode();
        setFormData({
          code: code,
          name: '',
          contact_name: '',
          email: '',
          phone: '',
          mobile: '',
          is_active: true,
        });
      } catch (err: any) {
        showError('Erreur lors de la génération du code');
        setFormData({
          code: '',
          name: '',
          contact_name: '',
          email: '',
          phone: '',
          mobile: '',
          is_active: true,
        });
      }
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplier(null);
    setFormError('');
  };

  const handleSaveSupplier = async () => {
    try {
      setFormError('');

      if (!formData.code.trim() || !formData.name.trim()) {
        setFormError('Le code et le nom sont obligatoires');
        return;
      }

      // Prepare data: convert empty strings to undefined for optional fields
      const preparedData = {
        ...formData,
        contact_name: formData.contact_name?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        mobile: formData.mobile?.trim() || undefined,
      };

      if (editingSupplier) {
        const updateData: UpdateSupplierRequest = preparedData;
        await suppliersAPI.update(editingSupplier.id, updateData);
        showSuccess('Fournisseur modifié avec succès');
      } else {
        await suppliersAPI.create(preparedData);
        showSuccess('Fournisseur créé avec succès');
      }

      handleCloseDialog();
      invalidate();
    } catch (err: any) {
      setFormError(extractErrorMessage(err));
    }
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    try {
      await suppliersAPI.delete(supplier.id);
      showSuccess('Fournisseur supprimé avec succès');
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleToggleStatus = async (supplier: Supplier) => {
    try {
      const result = await suppliersAPI.toggleStatus(supplier.id);
      showSuccess(result.message);
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  if (loading && suppliers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters sx={{ px: 0 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Gestion des Fournisseurs
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Suppliers"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Fournisseur
          </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Fournisseurs Actifs" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <>
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Rechercher par nom, code, email ou téléphone..."
          variant="filled"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Code
                  <ColumnFilter type="text" value={filters.code} onChange={(v) => update('code', v)} label="code" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  <ColumnFilter type="text" value={filters.name} onChange={(v) => update('name', v)} label="nom" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Contact
                  <ColumnFilter type="text" value={filters.contact_name} onChange={(v) => update('contact_name', v)} label="contact" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Email
                  <ColumnFilter type="text" value={filters.email} onChange={(v) => update('email', v)} label="email" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Téléphone
                  <ColumnFilter type="text" value={filters.phone} onChange={(v) => update('phone', v)} label="téléphone" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Mobile
                  <ColumnFilter type="text" value={filters.mobile} onChange={(v) => update('mobile', v)} label="mobile" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Statut
                  <ColumnFilter type="select" value={filters.is_active} onChange={(v) => update('is_active', v)} options={SUPPLIER_STATUT_OPTIONS} label="statut" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier.id}>
                <TableCell>{supplier.code}</TableCell>
                <TableCell>{supplier.name}</TableCell>
                <TableCell>{supplier.contact_name || '-'}</TableCell>
                <TableCell>{supplier.email || '-'}</TableCell>
                <TableCell>{supplier.phone || '-'}</TableCell>
                <TableCell>{supplier.mobile || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={supplier.is_active ? 'Actif' : 'Inactif'}
                    color={supplier.is_active ? 'success' : 'default'}
                    variant="filled"
                    size="small"
                    onClick={() => handleToggleStatus(supplier)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell>
                  <PermissionButton
                    moduleName="Config"
                    operation="edit"
                    miniModuleName="Suppliers"
                    component="IconButton"
                    onClick={() => handleOpenDialog(supplier)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </PermissionButton>
                  <PermissionButton
                    moduleName="Config"
                    operation="delete"
                    miniModuleName="Suppliers"
                    component="IconButton"
                    onClick={() => {
                      showConfirm(
                        `Êtes-vous sûr de vouloir supprimer le fournisseur "${supplier.name}" ?`,
                        () => handleDeleteSupplier(supplier),
                        {
                          title: 'Confirmer la suppression',
                          confirmText: 'Supprimer',
                          cancelText: 'Annuler'
                        }
                      );
                    }}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </PermissionButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalSuppliers}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {tabValue === 1 && (
      <>
      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>Date de Suppression</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : deletedSuppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucun fournisseur supprimé
                </TableCell>
              </TableRow>
            ) : (
              deletedSuppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell>{supplier.code}</TableCell>
                  <TableCell>{supplier.name}</TableCell>
                  <TableCell>{supplier.contact_name || '-'}</TableCell>
                  <TableCell>{supplier.email || '-'}</TableCell>
                  <TableCell>{supplier.phone || '-'}</TableCell>
                  <TableCell>{supplier.deleted_at ? new Date(supplier.deleted_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir restaurer le fournisseur "${supplier.name}" ?`,
                          () => handleRestoreSupplier(supplier),
                          {
                            title: 'Confirmer la restauration',
                            confirmText: 'Restaurer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="primary"
                      size="small"
                      title="Restaurer"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalDeletedSuppliers}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {/* Supplier Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSupplier ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography color="error.contrastText">{formError}</Typography>
            </Box>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="Code *"
                fullWidth
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
                disabled
                helperText={!editingSupplier ? "Code auto-généré" : "Code non modifiable"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="Nom *"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="Nom du contact"
                fullWidth
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="Téléphone"
                fullWidth
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="Mobile"
                fullWidth
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="filled"
                label="ICE"
                fullWidth
                value={formData.ice}
                onChange={(e) => setFormData({ ...formData, ice: e.target.value })}
                placeholder="Identifiant Commun de l'Entreprise"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="filled"
                label="Adresse"
                fullWidth
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSaveSupplier} variant="contained">
            {editingSupplier ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Container>
  );
};

export default SupplierManagement;